import { db } from '../idb';
import { invalidate } from '../cache';
import { env } from '../env';
import { newIdempotencyKey } from './idempotency';

type Method = 'POST' | 'PUT' | 'PATCH' | 'DELETE';
type Op = 'submit-duty-slip' | 'start-trip' | 'end-trip' | 'end-reading' | 'release-reading' | 'start-km' | 'add-expense' | 'edit-expense' | 'delete-expense' | 'trip-event';

export type QueueItem = {
  id: string;
  idempotencyKey: string;
  op: Op;
  url: string;
  method: Method;
  body: unknown;
  createdAt: number;
  attempts: number;
  lastAttemptAt?: number;
  lastError?: string;
  invalidatesOnSuccess?: string[];
};

export type ConflictDetail = {
  item: QueueItem;
  response: { error: string; currentState: string; yourState: string; message: string };
};

let conflictListener: ((c: ConflictDetail) => void) | null = null;
let errorListener: ((item: QueueItem, body: unknown) => void) | null = null;
let bannerListener: ((info: { queuedCount: number; draining: boolean }) => void) | null = null;

export function onConflict(fn: (c: ConflictDetail) => void) { conflictListener = fn; }
export function onError(fn: (item: QueueItem, body: unknown) => void) { errorListener = fn; }
export function onBanner(fn: (info: { queuedCount: number; draining: boolean }) => void) { bannerListener = fn; }

// Fired when a new item is enqueued so the sync scheduler can (re)start its
// retry loop immediately instead of waiting for the next online/foreground tick.
let kickListener: (() => void) | null = null;
export function onKick(fn: () => void) { kickListener = fn; }

export async function enqueue(
  args: Omit<QueueItem, 'id' | 'idempotencyKey' | 'createdAt' | 'attempts'> & { idempotencyKey?: string },
): Promise<QueueItem> {
  const { idempotencyKey, ...rest } = args;
  const key = idempotencyKey ?? newIdempotencyKey();
  const item: QueueItem = {
    id: key,
    idempotencyKey: key,
    createdAt: Date.now(),
    attempts: 0,
    ...rest,
  };
  const handle = await db();
  await handle.put('pendingMutations', item);
  notifyBanner();
  kickListener?.();
  return item;
}

export async function hasQueuedItems(): Promise<boolean> {
  const handle = await db();
  const count = await handle.count('pendingMutations');
  return count > 0;
}

/** All not-yet-synced mutations, oldest first. Trip detail uses this to
 *  overlay queued transitions onto server state so a driver who started a
 *  trip offline (or mid-flaky-network) still sees it as started after an
 *  app restart — the write is durable in IDB even before the server has it. */
export async function pendingMutations(): Promise<QueueItem[]> {
  const handle = await db();
  // idb's store typing widens `op` to string — the store only ever holds
  // QueueItems written by enqueue() above.
  const items = (await handle.getAll('pendingMutations')) as QueueItem[];
  return items.sort((a, b) => a.createdAt - b.createdAt);
}

/** Remove queued writes for a booking that is no longer assigned to this
 * driver. Matching accepts UUID or booking-code route segments. */
export async function discardPendingMutationsForBooking(bookingIdOrCode: string): Promise<void> {
  const handle = await db();
  const items = (await handle.getAll('pendingMutations')) as QueueItem[];
  const marker = `/bookings/${bookingIdOrCode}/`;
  const tx = handle.transaction('pendingMutations', 'readwrite');
  await Promise.all(items.filter(item => item.url.includes(marker)).map(item => tx.store.delete(item.id)));
  await tx.done;
  await notifyBanner();
}

async function notifyBanner() {
  if (!bannerListener) return;
  const handle = await db();
  const count = await handle.count('pendingMutations');
  bannerListener({ queuedCount: count, draining: false });
}

let draining = false;
let lastDrainAt = 0;
const DRAIN_THROTTLE_MS = 3000;

export type DrainResult = { remaining: number; lastFailed: boolean };

export async function drainQueue(): Promise<DrainResult> {
  if (draining) {
    const h = await db();
    return { remaining: await h.count('pendingMutations'), lastFailed: false };
  }
  if (Date.now() - lastDrainAt < DRAIN_THROTTLE_MS) {
    const h = await db();
    return { remaining: await h.count('pendingMutations'), lastFailed: false };
  }
  draining = true;
  lastDrainAt = Date.now();

  const handle = await db();
  // IDB schema stores op as `string` (flexible) but we always write QueueItem
  // values, so casting here is safe. The stored shape is a strict superset of
  // the schema's minimal declaration.
  const rawItems = await handle.getAllFromIndex('pendingMutations', 'by_createdAt');
  const items = rawItems as unknown as QueueItem[];
  const base = env().VITE_API_BASE.replace(/\/+$/, '');

  let lastFailed = false;
  for (const item of items) {
    if (bannerListener) bannerListener({ queuedCount: items.length, draining: true });
    try {
      const res = await fetch(`${base}/v1${item.url}`, {
        method: item.method,
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': item.idempotencyKey,
        },
        body: JSON.stringify(item.body),
        credentials: 'include',
      });
      if (res.ok) {
        await handle.delete('pendingMutations', item.id);
        if (item.invalidatesOnSuccess) {
          for (const pattern of item.invalidatesOnSuccess) {
            await invalidate(pattern);
          }
        }
      } else if (res.status === 409) {
        const body = await res.json().catch(() => ({}));
        await handle.delete('pendingMutations', item.id);
        if (conflictListener) conflictListener({ item, response: body });
      } else if (res.status >= 500) {
        item.attempts += 1;
        item.lastAttemptAt = Date.now();
        item.lastError = await res.text().catch(() => `${res.status}`);
        await handle.put('pendingMutations', item);
        lastFailed = true;
        break;
      } else {
        const body = await res.json().catch(() => ({}));
        await handle.delete('pendingMutations', item.id);
        if (errorListener) errorListener(item, body);
      }
    } catch (e) {
      item.attempts += 1;
      item.lastAttemptAt = Date.now();
      item.lastError = String(e);
      await handle.put('pendingMutations', item);
      lastFailed = true;
      break;
    }
  }

  draining = false;
  notifyBanner();
  const remaining = await handle.count('pendingMutations');
  return { remaining, lastFailed };
}
